import Table from "../../components/Table/Table";
import { useContext } from "react";
import PageTitle from "@/components/PageTitle";
import { useNavigate } from "react-router-dom";
import { ModalProvider } from "../../components/Modal/Modal";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
// import Pagination from "../../components/pagination/IndexPagination";
import Spinner from "../../components/spinner/Spinner";
import { defaultKursusColumns } from "../../config/kursusConfig";
import { PagedKursusQueryContext } from "../../context/PagedKursusQueryProvider";
import { KursusLinjeNavHeader } from "./KursusLinjeLayoutCommon";
import FiltersKursus from "../../components/filter/FiltersKursus";
import { QUERY_PAGE_SIZE } from "../../api/util";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../../components/ErrorFallback";
import { defaultSetSelected } from "../../context/PagedLinjeQueryProvider";
import { IRowData, SortBy } from "../../interfaces/table";
import { PagedKursusQueryField } from "../../interfaces/kursus";
import useLocalStorageState from "use-local-storage-state";
import { kursusIndexSortOrderKey } from "../../util/localStorageKeys";
import { Notification, useToaster } from "rsuite";
import DownloadPendingIcon from "../../components/icons/DownloadPendingIcon";
import { downloadIndexTableToExcel } from "../../util/tableExportUtils";
import { useSelectAllErrorToast } from "../../hooks/useSelectAllErrorToast";
import WarningIcon from "../../components/icons/WarningIcon";

const defaultSortBy: Array<SortBy<PagedKursusQueryField>> = [
  {
    field: "fraDato",
    direction: "desc",
  },
];

const Kursus = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <PageTitle title="Kursusregister" />
      <div className="l-main-row">
        <FiltersKursus />
        <Main />
      </div>
    </ErrorBoundary>
  );
};

const Main = () => {
  const pagedKursusQueryContext = useContext(PagedKursusQueryContext);
  const {
    columns,
    error,
    loading,
    setActiveColumns,
    setQuery,
    pagedResponse,
    query,
    page,
    selection,
    selectAllError,
    clearSelectAllError,
  } = pagedKursusQueryContext;

  const {
    addSelectedIds,
    clearSelection,
    removeSelectedIds,
    selectAll,
    selectedIds,
    lastSelectedId,
    setLastSelectedId,
  } = selection;

  const navigate = useNavigate();

  // for all your toasting purposes
  const toaster = useToaster();
  useSelectAllErrorToast(selectAllError, clearSelectAllError);

  const [sortBy, setSortBy] = useLocalStorageState<Array<SortBy>>(
    kursusIndexSortOrderKey,
    { defaultValue: defaultSortBy },
  );

  if (!pagedResponse) return null;
  const { kursusholds } = pagedResponse;
  const { edges, totalCount, pageInfo } = kursusholds;
  const { endCursor, startCursor, hasNextPage, hasPreviousPage } = pageInfo;

  if (error) return <div>noget gik galt</div>;
  if (loading && !pagedResponse) return <Spinner label="Henter data" />;

  const isAllSelected = totalCount === selectedIds.length;

  function downloadToExcel() {
    if (query) {
      toaster.push(
        <Notification>
          <div className="d-flex flex-row gap-3 align-items-center">
            <span className={`c-theme-icon c-theme-icon--kursus`}>
              <DownloadPendingIcon />
            </span>
            Forbereder fil download
          </div>
        </Notification>,
        {
          placement: "bottomEnd",
          duration: 2500,
        },
      );

      const promise = downloadIndexTableToExcel(
        selectedIds,
        query,
        "kursusholds",
        columns,
        "export-kursushold",
      );

      promise.catch((error) => {
        toaster.push(
          <Notification>
            <div className="d-flex flex-row gap-3 align-items-center">
              <span className={`c-theme-icon bg-danger text-white`}>
                <WarningIcon />
              </span>
              <div className="d-flex flex-column gap-1">
                Der skete en fejl, da vi forberedte filen. Prøv igen eller
                kontakt deres administrator.
                <br />
                <small className="text-muted">
                  {error.status && `Fejl: ${error.status}`}
                </small>
              </div>
            </div>
          </Notification>,
          {
            placement: "bottomEnd",
            duration: 8000,
          },
        );
      });
    }
  }

  return (
    <main className="l-main__content">
      <KursusLinjeNavHeader
        type="kurser"
        pageInfo={pageInfo}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        totalCount={totalCount}
        onPreviousClick={() => {
          setQuery({
            ...query,
            page: page - 1,
            after: undefined,
            before: startCursor,
          });
        }}
        onNextClick={() => {
          setQuery({
            ...query,
            page: page + 1,
            before: undefined,
            after: endCursor,
          });
        }}
        page={page}
        allSelected={isAllSelected}
        selectedIds={selectedIds}
        downloadToExcel={downloadToExcel}
      />
      <div className="l-main__table px-2">
        <ModalProvider>
          <Table
            onRowClick={(data: IRowData) => {
              navigate(`/kursus/${data.id}`);
            }}
            className="table--selectable"
            columns={columns}
            keyField="id"
            data={edges.map((e) => {
              return {
                ...e.node,
                selected: selectedIds.indexOf(e.node.id) > -1,
              };
            })}
            selectRow={{
              setSelected: (rowData, selected, shiftKeyPressed) => {
                defaultSetSelected(
                  rowData,
                  selected,
                  shiftKeyPressed,
                  edges,
                  lastSelectedId,
                  addSelectedIds,
                  removeSelectedIds,
                  setLastSelectedId,
                );
              },
              toggleSelectAllOnPage: (mode) => {
                if (mode === "all") {
                  addSelectedIds(edges.map((e) => e.node.id));
                } else {
                  removeSelectedIds(edges.map((e) => e.node.id));
                }
              },
              isAllSelected: isAllSelected,
              selectAll,
              clearSelection,
            }}
            pageInfo={{
              pageSize: QUERY_PAGE_SIZE,
              totalNumberOfRows: totalCount,
            }}
            sortings={{
              defaultSortBy: defaultSortBy,
              setSortBy: setSortBy,
              sortBy: sortBy,
            }}
          />
          <ColumnEditorModal
            columns={columns}
            setActiveColumns={setActiveColumns}
            defaultColumns={defaultKursusColumns}
          />
        </ModalProvider>
      </div>
    </main>
  );
};

export default Kursus;
